import { NavigateFunction, useParams } from "react-router-dom";
import shallow from "zustand/shallow";
import { chunkString, DefaultValuesDto, LandCode, useDefaultValues } from "./defaultValues";
import { EnumItemDto } from "./enum";
import { keyBy } from "lodash";
import { UseListStore } from "./listStore";
import { BehaviorSubject, combineLatest, Observable, Subscription } from "rxjs";
import { useCallback, useMemo, useState, useSyncExternalStore } from "react";
import { dayjs } from "./config";
import { useStateWithDeps } from "use-state-with-deps";
import { UseEntityStore } from "./entityStore";
import { createPortal } from "react-dom";

export type ArrayElement<ArrayType extends readonly unknown[]> =
    ArrayType extends readonly (infer ElementType)[] ? ElementType : never;

export function delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

// https://stackoverflow.com/questions/51476103/enforcing-type-of-key-in-generic-function-input
export type KeysOfTypeSet<T, U> = { [K in keyof T]: T[K] extends U ? K : never }[keyof T] & string;
export type KeysOfTypeGet<T, U> = { [K in keyof T]: U extends T[K] ? K : never }[keyof T] & string;
export type KeysOfType<T, U> = { [K in keyof T]: T[K] extends U ? U extends T[K] ? K : never : never }[keyof T] & string;

export function ifNull<T>(value: T | null | undefined, other: T) {
    return value == null ? other : value;
}

export function containsDigit(text: string) {
    return /\d/.test(text);
}

export function isDigitsOnly(text: string) {
    return /^\d*$/.test(text);
}

export function getDecimalPlaces(value: number) {
    const decimals = value.toString().split(".").at(1);
    return decimals?.length || 0;
}

export function landCodes(...landCodes: LandCode[]) {
    return Array<LandCode | undefined>(...landCodes);
}

export const isoDateFormat = "YYYY-MM-DD";
export const isoMonthFormat = "YYYY-MM";

let lastKey = 0;

export function withSameParam<T>(paramName: string, component: (changingParam: (newValue: string) => void) => (props: T) => JSX.Element) {
    let values: (string | undefined)[] = [];
    let key = ++lastKey;
    let isInitialized = false;
    const Comp = component(newValue => {
        values.push(newValue);
    });
    return (props: T): JSX.Element => {
        const paramValue = useParams()[paramName];
        if (!isInitialized) {
            isInitialized = true;
            values.push(paramValue);
        }
        if (!values.some(p => shallow(p, paramValue))) {
            values = [paramValue];
            key = ++lastKey;
        }
        return <Comp {...props} key={key} />;
    }
}


export function withSameProp<T, U extends keyof T>(propName: U, component: (changingProp: (newValue: T[U]) => void) => (props: T) => JSX.Element) {
    let values: T[U][] = [];
    let key = ++lastKey;
    let isInitialized = false;
    const Comp = component(newValue => {
        values.push(newValue);
    });
    return (props: T): JSX.Element => {
        const propValue = props[propName];
        if (!isInitialized) {
            isInitialized = true;
            values.push();
        }
        if (!values.some(p => shallow(p, propValue))) {
            values = [propValue];
            key = ++lastKey;
        }
        return <Comp {...props} key={key} />;
    }
}

export function getLanguagesRaw(def: DefaultValuesDto) {
    return chunkString(def.language || "", 2);
}

export function getLanguages(def: DefaultValuesDto): EnumItemDto[] {
    return getLanguagesRaw(def).map(p => ({ id: p, name: p }));
}

export function leftJoin<A, B, K extends string | number, R>(a: A[], b: B[], getAKey: (item: A) => K, getBKey: (item: B) => K, getResult: (a: A, b: B | undefined) => R) {
    const bd = keyBy(b, b => getBKey(b));
    return a.map(a => getResult(a, bd[getAKey(a)]));
}

export function addOrUpdateItem<T, K>(useStore: UseListStore<T>, getId: (item: T) => K, item: T, addToEnd = true) {
    const store = useStore.getState();
    const itemId = getId(item);
    let items = store.items;
    if (items.some(p => getId(p) === itemId)) {
        items = items.map(p => getId(p) === itemId ? item : p);
    } else {
        items = addToEnd ? items.concat(item) : [item].concat(items);
    }
    store.setItems(items, false);
    store.setSelectedItem(item);
}

export function useRxJsSub(createSub: () => Subscription, onUnsubscribe?: () => void) {
    const [sub] = useState(() => () => {
        const sub = createSub();
        return () => {
            sub.unsubscribe();
            onUnsubscribe?.();
        }
    });
    useSyncExternalStore(sub, () => null);
}

export function useRxJsSubWithState<T>(createSub: () => [Subscription, T], onUnsubscribe: (data: T) => void) {
    const [sub] = useState(() => () => {
        const sub = createSub();
        return () => {
            sub[0].unsubscribe();
            onUnsubscribe?.(sub[1]);
        }
    });
    useSyncExternalStore(sub, () => null);
}

export function useRxJsSubWithDeps(createSub: () => Subscription, deps: React.DependencyList) {
    const [sub] = useStateWithDeps(() => () => {
        const sub = createSub();
        return () => sub.unsubscribe();
    }, deps);
    useSyncExternalStore(sub, () => null);
}

export function useBehaviorSubject<T>(behaviorSubject: BehaviorSubject<T>) {
    return useSyncExternalStore(onChange => {
        const sub = behaviorSubject.subscribe({ next(v) { onChange(); } });
        return () => sub.unsubscribe();
    }, () => behaviorSubject.value);
}

export function useRxJsObservablesWatcher(observables: Observable<any>[]) {
    const observable = useMemo(() => combineLatest(observables), [observables]);
    const [id, setId] = useState(0);
    useRxJsSubWithDeps(() => observable.subscribe(p => {
        setId(id => id + 1);
    }), [observable]);
    return id;
}

export function useRxJsBehaviorSubject<T>(subject: BehaviorSubject<T>) {
    const [value, setValue] = useState<T>(subject.value);
    useRxJsSubWithDeps(() => subject.subscribe(setValue), [subject]);
    return value;
}

export function fromToday(transform: (today: dayjs.Dayjs) => dayjs.Dayjs, format = isoDateFormat) {
    return transform(dayjs.tz(undefined)).format(format);
}

export function today() {
    return dayjs.tz(undefined).format(isoDateFormat);
}

export function fixLeafletMarkerImageLoading() {
    const L = require("leaflet");

    delete L.Icon.Default.prototype._getIconUrl;

    L.Icon.Default.mergeOptions({
        iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
        iconUrl: require("leaflet/dist/images/marker-icon.png"),
        shadowUrl: require("leaflet/dist/images/marker-shadow.png")
    });
}

export function openCustomerInNewWindow(custId: string) {
    window.open(`/customers/${custId}`);
}

export function openCustomerInSameWindow(custId: string, navigate: NavigateFunction) {
    navigate(`/customers/${custId}`);
}

let id = 0;

export function newId() {
    return --id;
}

export function sanitize(value: string) {
    const overrideDict = useDefaultValues.getState().clientData.overrideCharactersDict;
    return value.split("").map(char => overrideDict[char] ?? char).join("");
}

export function capitalizeFirstLetter(val: string) {
    return String(val).charAt(0).toUpperCase() + String(val).slice(1);
}

export function propCapitalizeFirstLetter<T>(propName: KeysOfType<T, string | null | undefined>, useStore: UseEntityStore<T>) {
    useStore.getState().setClientDataProp(propName,
        capitalizeFirstLetter(useStore.getState().clientData[propName] as string ?? "") as T[KeysOfType<T, string | null | undefined>]);
}

export function usePortal(content: JSX.Element | undefined) {
    const [ref, setRef] = useState<HTMLDivElement>();
    const portal = useCallback(() => <div ref={ref => setRef(ref ?? undefined)}></div>, []);
    return { portal, render: () => ref && content && createPortal(content, ref) };
}

